import React, { useEffect, useState } from "react"
import {
    Box,
    Button,
    Chip,
    FormControlLabel,
    Grid,
    MenuItem,
    Paper,
    Switch,
    TextField,
    Typography,
} from "@material-ui/core"
import CloudDoneIcon from "@material-ui/icons/CloudDone"
import SaveIcon from "@material-ui/icons/Save"
import { useSnackbar } from "../../../utils/notificacoes"
import {
    obterPickngo,
    salvarPickngo,
    testarPickngo,
    type IntegracaoPickngoDados,
} from "../../../services/integracaoParceiros"
import useLoadingStore from "../../../stores/LoadingStore"
import { useIntegracaoStyles } from "../../../utils/integracaoParceirosStyles"
import { PICKNGO_LOGO_URL } from "../assets"

type Ambiente = "homologacao" | "producao"

const inicial: IntegracaoPickngoDados = {
    ativo: false,
    ambiente: "homologacao",
    credenciaisHomologacao: false,
    credenciaisProducao: false,
}

export function IntegracaoPickngoForm() {
    const classes = useIntegracaoStyles()
    const { enqueueSnackbar } = useSnackbar()
    const setLoading = useLoadingStore(s => s.setLoading)

    const [dados, setDados] = useState<IntegracaoPickngoDados>(inicial)
    const [statusTeste, setStatusTeste] = useState<{ ativa: boolean; mensagem: string } | null>(null)

    const alertStart = (msg: string, tipo: "success" | "error" | "info") =>
        enqueueSnackbar(msg, { variant: tipo })

    const aplicar = (res: IntegracaoPickngoDados) =>
        setDados({ ...res, ambiente: res.ambiente === "producao" ? "producao" : "homologacao" })

    useEffect(() => {
        const carregar = async () => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) return
            setLoading(true)
            try {
                const res = await obterPickngo(autorizacao)
                if ("retornoErro" in res) {
                    alertStart(res.mensagem || "Erro ao carregar integração", "error")
                } else {
                    aplicar(res)
                }
            } finally {
                setLoading(false)
            }
        }
        carregar()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const credenciaisDoAmbiente =
        dados.ambiente === "producao" ? dados.credenciaisProducao : dados.credenciaisHomologacao

    const handleSalvar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        if (dados.ativo && !credenciaisDoAmbiente) {
            alertStart(
                "Não há credenciais da Pick'n Go para este ambiente. Fale com o suporte antes de ativar.",
                "error"
            )
            return
        }
        setLoading(true)
        try {
            const res = await salvarPickngo(autorizacao, { ativo: dados.ativo, ambiente: dados.ambiente })
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao salvar", "error")
            } else {
                aplicar(res)
                setStatusTeste(null)
                alertStart(
                    "Configuração salva! Saia e entre de novo no painel para o botão aparecer nos pedidos.",
                    "success"
                )
            }
        } finally {
            setLoading(false)
        }
    }

    const handleTestar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setLoading(true)
        try {
            const res = await testarPickngo(autorizacao)
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao testar", "error")
                return
            }
            setStatusTeste({ ativa: res.ativa, mensagem: res.mensagem })
            alertStart(res.mensagem, res.ativa ? "success" : "error")
        } finally {
            setLoading(false)
        }
    }

    const rotuloCredencial = (ok: boolean) => (ok ? "cadastradas" : "não cadastradas")

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                    <img src={PICKNGO_LOGO_URL} alt="Pick'n Go" style={{ height: 40, borderRadius: 6 }} />
                    <Typography variant="h5" className={classes.titleText}>
                        Integração Pick'n Go
                    </Typography>
                </Box>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Configuração
                        </Typography>
                        {statusTeste && (
                            <Chip
                                size="small"
                                label={statusTeste.ativa ? "Conexão OK" : "Falha"}
                                style={{
                                    marginLeft: 12,
                                    backgroundColor: statusTeste.ativa ? "#4caf50" : "var(--cor-erro)",
                                    color: "white",
                                    fontWeight: 600,
                                }}
                            />
                        )}
                    </Box>
                    <Box className={classes.cardContent}>
                        <Typography variant="body2" style={{ color: "#555", marginBottom: 16 }}>
                            Com a integração ativa, os pedidos de delivery ganham a opção{" "}
                            <strong>Entrega Pick'n Go</strong> no painel de pedidos: cotar, solicitar
                            entregador, acompanhar e cancelar. Use <strong>Homologação</strong> para testes
                            e <strong>Produção</strong> para valer.
                        </Typography>
                        {!dados.credenciaisHomologacao && !dados.credenciaisProducao && (
                            <Typography variant="body2" style={{ color: "var(--cor-erro)", marginBottom: 12 }}>
                                As credenciais da Pick'n Go ainda não foram cadastradas. Fale com o suporte
                                antes de ativar.
                            </Typography>
                        )}
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Ambiente"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.ambiente}
                                    onChange={e =>
                                        setDados(prev => ({ ...prev, ambiente: e.target.value as Ambiente }))
                                    }
                                    helperText={`Homologação: ${rotuloCredencial(
                                        dados.credenciaisHomologacao
                                    )} · Produção: ${rotuloCredencial(dados.credenciaisProducao)}`}
                                >
                                    <MenuItem value="homologacao">Homologação (testes)</MenuItem>
                                    <MenuItem value="producao">Produção</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={dados.ativo}
                                            onChange={e =>
                                                setDados(prev => ({ ...prev, ativo: e.target.checked }))
                                            }
                                        />
                                    }
                                    label="Ativo"
                                />
                            </Grid>
                        </Grid>

                        {statusTeste && (
                            <Typography
                                variant="body2"
                                style={{
                                    marginTop: 8,
                                    color: statusTeste.ativa ? "#2e7d32" : "var(--cor-erro)",
                                }}
                            >
                                {statusTeste.mensagem}
                            </Typography>
                        )}

                        <Box display="flex" justifyContent="flex-end" mt={2} style={{ gap: 12 }}>
                            <Button
                                className={classes.secondaryButton}
                                variant="outlined"
                                startIcon={<CloudDoneIcon />}
                                onClick={handleTestar}
                            >
                                Testar conexão
                            </Button>
                            <Button
                                className={classes.saveButton}
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSalvar}
                            >
                                Salvar
                            </Button>
                        </Box>
                        <Typography
                            variant="caption"
                            style={{ color: "#7f8c8d", display: "block", marginTop: 8 }}
                        >
                            O "Testar conexão" autentica na Pick'n Go no ambiente salvo — salve antes de
                            testar.
                        </Typography>
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}
